import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';

const TABS = [
  { key: 'day', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all', label: 'All time' },
];

export default function Sales() {
  const { getToken } = useAuth();
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('day');

  const fetchSales = useCallback(async () => {
    const res = await api('/api/owners/me/sales', { getToken });
    if (!res.ok) {
      if (res.status === 403) {
        setError('Not authorized');
        return;
      }
      setError('Failed to load sales');
      return;
    }
    const data = await res.json();
    setSales(data);
    setError(null);
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchSales();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchSales]);

  if (loading && !sales) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-700 dark:text-red-300">{error}</p>
        <Link to="/owner/dashboard" className="mt-4 inline-block text-amber-600 hover:text-amber-700">← Back</Link>
      </div>
    );
  }

  const formatCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  const periodData = {
    day: {
      cents: sales?.dailyCents ?? 0,
      orders: sales?.dailyOrders ?? 0,
      chart: sales?.dailyChart ?? [],
      items: sales?.dailyItemSales ?? [],
    },
    week: {
      cents: sales?.weeklyCents ?? 0,
      orders: sales?.weeklyOrders ?? 0,
      chart: sales?.weeklyChart ?? [],
      items: sales?.weeklyItemSales ?? [],
    },
    month: {
      cents: sales?.monthlyCents ?? 0,
      orders: sales?.monthlyOrders ?? 0,
      chart: sales?.monthlyChart ?? [],
      items: sales?.monthlyItemSales ?? [],
    },
    all: {
      cents: sales?.totalCents ?? 0,
      orders: sales?.totalOrders ?? 0,
      chart: sales?.allTimeChart ?? [],
      items: sales?.allTimeItemSales ?? [],
    },
  };

  const data = periodData[activeTab] || periodData.day;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/owner/dashboard" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-1 inline-block">
            ← Back to dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Sales
          </h1>
        </div>
        <button
          type="button"
          onClick={fetchSales}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          Refresh
        </button>
      </div>

      <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-3 rounded-lg font-medium min-h-[48px] ${
              activeTab === tab.key
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Revenue ({activeTab === 'day' ? 'Today' : activeTab === 'week' ? 'This week' : activeTab === 'month' ? 'This month' : 'All time'})
          </p>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
            {formatCents(data.cents)}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {data.orders} orders
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Items sold
          </p>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
            {data.items.reduce((sum, i) => sum + (i.quantity || 0), 0)}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {data.items.length} different items
          </p>
        </div>
      </div>

      {data.chart && data.chart.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-4">
            Revenue by period
          </p>
          <BarChart data={data.chart} formatCents={formatCents} />
        </div>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-4">
          Food sold
        </p>
        {data.items.length === 0 ? (
          <p className="text-slate-500 dark:text-slate-400 py-6 text-center">
            No items sold in this period.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.items.map((item, idx) => (
              <li
                key={idx}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-700/50"
              >
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.name}</span>
                <span className="text-amber-600 dark:text-amber-400 font-semibold">
                  {item.quantity} sold
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function BarChart({ data, formatCents }) {
  const maxCents = Math.max(1, ...data.map((d) => d.cents || 0));
  return (
    <div className="space-y-3">
      {data.map((bucket, idx) => {
        const pct = maxCents > 0 ? Math.round(((bucket.cents || 0) / maxCents) * 100) : 0;
        return (
          <div key={idx} className="flex flex-col gap-1">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">{bucket.label}</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {formatCents(bucket.cents)}
              </span>
            </div>
            <div className="h-6 rounded-lg bg-slate-200 dark:bg-slate-600 overflow-hidden">
              <div
                className="h-full rounded-lg bg-amber-500 transition-all duration-300 min-w-[2px]"
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
